import { OmitUnion } from "@redotech/util/type";
import { AdvancedFlowTokenType as AdvancedFlowConditionTokenType } from "./data-types/base-types";
import { GenericDataType, GenericPrefixes } from "./data-types/generic-types";
import { DataType, DataTypeName } from "./data-types/index";

/**
 * Infers the true type of a schema's field by recursively unwrapping generic prefixes.
 */
type Value<T> = T extends `${GenericPrefixes["Array"]}${infer U}`
  ? Value<U>[]
  : T extends `${GenericPrefixes["Optional"]}${infer V}`
    ? Value<V> | undefined
    : T extends DataTypeName
      ? DataType[T]
      : never;

export interface SchemaFields {
  [key: string]: OmitUnion<SchemaField, "key">;
}

/**
 * The generic type for datatypes that can be passed as input to an email flow.
 *
 * @example
 * const customerEventSchema = defineSchema({
 *  fields: {
 *    email: {
 *    dataType: "Email",
 *    documentation: "The email address of the customer who triggered the event",
 *  },
 * });
 */
export interface Schema {
  fields: SchemaFields;
}

export type SchemaField = {
  key: string;
  documentation: string;
  prettyValues?: Record<string, string>;
  /**
   * If true, hides the field from the "insert variable" modal in the email/SMS builders.
   * Typically only needs to be set for textlike data types.
   */
  hideFromInsertionModal?: boolean;
  /**
   * If true, hides the field from the condition step in the advanced flow builder.
   */
  hideFromConditionStep?: boolean;
  /**
   * Optional predicate function to conditionally include this field based on enabled integrations.
   * If undefined, the field is always included.
   * If defined, the field is only included when the predicate returns true.
   */
  shouldInclude?: (enabledIntegrations: Record<string, boolean>) => boolean;
} & (
  | {
      dataType: Exclude<GenericDataType, "Enum" | "Token" | "Text">;
      values?: string[];
    }
  | { dataType: "Enum"; values: string[] }
  | { dataType: "Token"; tokenType: AdvancedFlowConditionTokenType }
  | {
      dataType: "Text";
      /**
       * Identifier for the type of suggestions to fetch.
       * The UI component will map this to the appropriate RPC call.
       */
      suggestionType?: "orderSource";
    }
);
/**
 * A helper function to define a schema that provides increased type safety.
 * Without using it, the type of the schema is inferred broadly,
 * thus making it difficult to hardcode example schemas.
 */
export function defineSchema<T extends Schema>(schema: T) {
  return schema;
}

/**
 * An instance of a Schema.
 *
 * @example
 * const customerEvent<SchemaInstance<typeof customerEventSchema>> = {
 *  fields: {
 *    email: "john@fake.com"
 *  }
 * };
 */
export type SchemaInstance<T extends Schema> = {
  [K in keyof T["fields"]]: Value<T["fields"][K]["dataType"]>;
};
